import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "react-feather";
import axios from "axios";
import SubscriptionBannerIllustration from "./SubscriptionBannerIllustration";

const API_URL = process.env.REACT_APP_BASEURL || "http://localhost:8000/api/";
const MILESTONE_KEY_PREFIX = "dismissed_expiry_milestone_";
const DAILY_KEY_PREFIX = "dismissed_expiry_daily_";

const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN);

export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getBannerMilestone = (plan = {}) => {
  const days = Number(plan.days_until_renewal);
  if (plan.is_suspended) return -99;
  if (plan.is_in_retry_grace) return -98;
  if (plan.is_expire || plan.billing_status === "expired") return 0;
  if (plan.days_until_renewal === null || plan.days_until_renewal === undefined) return null;
  if (days <= 5) return 5;
  if (days <= 10) return 10;
  if (days <= 15) return 15;
  if (days <= 30) return 30;
  if (days <= 45) return 45;
  return null;
};

export const getDismissalScope = (plan = {}) =>
  `${plan.current_plan_key || plan.plan || "plan"}_${plan.expiry_date || "unknown"}`;

export const cleanupExpiredDismissals = (
  activeScope,
  todayStr = getLocalDateString(),
  storage = localStorage
) => {
  if (!storage || !activeScope) return;
  const staleKeys = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (
      key &&
      (key.startsWith(MILESTONE_KEY_PREFIX) || key.startsWith(DAILY_KEY_PREFIX)) &&
      (!key.includes(activeScope) ||
        (key.startsWith(DAILY_KEY_PREFIX) && !key.endsWith(`_${todayStr}`)))
    ) {
      staleKeys.push(key);
    }
  }

  staleKeys.forEach((key) => storage.removeItem(key));
};

export const getSubscriptionBannerConfig = ({ plan = {} }) => {
  const milestone = getBannerMilestone(plan);
  const upcoming = plan.upcoming_subscription;
  const days = Number(plan.days_until_renewal);
  const expiry = plan.expiry_date || "the scheduled date";
  const invoiceRenewal = Boolean(plan.invoice_renewal);

  if (upcoming) {
    return {
      type: "scheduled",
      milestone,
      themeType: "success",
      iconType: "scheduled",
      badge: "Renewal Scheduled",
      title: "Upcoming Renewal Paid & Scheduled",
      description: `Your current plan is active through ${expiry}. ${upcoming.plan_name} begins automatically on ${upcoming.starts_at}.`,
    };
  }
  if (milestone === null) return null;

  if (plan.is_suspended) {
    return {
      type: "suspended",
      milestone,
      themeType: "danger",
      iconType: "suspended",
      badge: "Action Required",
      title: "Account Suspended Due to Unpaid Renewal",
      description: "Your account access is currently paused. Pay your renewal invoice to restore immediate campaign and dietitian access.",
    };
  }

  if (plan.is_in_retry_grace) {
    const attempt = Number(plan.retry_attempt || 0);
    const isFinalRetry = attempt >= 2;
    return {
      type: "retry",
      milestone,
      themeType: isFinalRetry ? "danger" : "warning",
      iconType: isFinalRetry ? "danger" : "warning",
      badge: attempt > 0 ? `Retry ${attempt} of ${plan.retry_max_attempts || 3} Failed` : "Renewal Charge Failed",
      title: isFinalRetry ? "Final Payment Retry Remaining" : "Automatic Renewal Payment Failed",
      description: `Your account remains fully active during the payment grace period. ${plan.retry_grace_days_remaining || 0} days remain before suspension${plan.next_retry_at ? `; the next retry is scheduled for ${plan.next_retry_at}` : ""}. Update billing or pay by invoice to avoid interruption.`,
    };
  }

  if (milestone === 0) {
    return {
      type: "expired",
      milestone,
      themeType: "danger",
      iconType: "danger",
      badge: "Expired",
      title: "Subscription Expired",
      description: `Your subscription cycle ended on ${expiry}. Request an invoice or renew to resume active outreach.`,
    };
  }

  if (milestone === 5 || milestone === 10) {
    return {
      type: "expiring_final_10",
      milestone,
      themeType: "danger",
      iconType: "danger",
      badge: days <= 0 ? "Ends Today" : `${days} Days Left`,
      title: invoiceRenewal
        ? days <= 0 ? "Renewal Invoice Due Today" : `Renewal Invoice Due in ${days} Days`
        : days <= 0 ? "Card Auto-Renews Today" : `Card Auto-Renews in ${days} Days`,
      description: invoiceRenewal
        ? `Your renewal invoice is due on ${expiry}. Complete payment online to maintain uninterrupted campaign access.`
        : `Your payment method on file will be charged automatically on ${expiry}. You can switch to invoice payment or update your card.`,
    };
  }

  if (milestone === 15) {
    return {
      type: "expiring_15",
      milestone,
      themeType: "warning",
      iconType: "warning",
      badge: "15 Days Left",
      title: "Subscription Renews in 15 Days",
      description: `Your current subscription period concludes on ${expiry}. Pay early by corporate invoice (Net 30) or review your renewal plan.`,
    };
  }

  if (milestone === 30) {
    return {
      type: "expiring_30",
      milestone,
      themeType: "primary",
      iconType: "clock",
      badge: "30 Days Left",
      title: "Subscription Renews in 30 Days",
      description: `Your ${plan.plan ? `${plan.plan} ` : ""}subscription cycle ends on ${expiry}. You can pay by corporate invoice (Net 30) or explore annual upgrade savings.`,
    };
  }

  return {
    type: "expiring_45",
    milestone,
    themeType: "info",
    iconType: "calendar",
    badge: "45 Days Notice",
    title: "Subscription Renews in 45 Days",
    description: `Your subscription period will renew on ${expiry}. Early renewal and annual discount options are available.`,
  };
};

export const checkIsDismissed = ({ config, plan = {}, mode = "dashboard", todayStr = getLocalDateString(), storage = localStorage }) => {
  if (!config || mode !== "dashboard") return false;
  if (plan.upcoming_subscription || plan.is_suspended || plan.is_in_retry_grace) return false;

  const scope = getDismissalScope(plan);
  const days = Number(plan.days_until_renewal);
  const isFinal10Days = days >= 0 && days <= 10 && !plan.is_expire;
  if (isFinal10Days) {
    return Boolean(storage.getItem(`${DAILY_KEY_PREFIX}${scope}_${todayStr}`));
  }

  const localMilestone = storage.getItem(`${MILESTONE_KEY_PREFIX}${scope}`);
  const backendMilestone = plan.renewal_prompt_dismissed;
  return localMilestone === String(config.milestone) || (
    backendMilestone !== null &&
    backendMilestone !== undefined &&
    Number(backendMilestone) === config.milestone
  );
};

export const dismissBannerStage = async ({ config, plan = {}, todayStr = getLocalDateString(), storage = localStorage, token = getToken(), apiUrl = API_URL }) => {
  if (!config || plan.upcoming_subscription || plan.is_suspended || plan.is_in_retry_grace) return;
  const scope = getDismissalScope(plan);
  const days = Number(plan.days_until_renewal);
  const isFinal10Days = days >= 0 && days <= 10 && !plan.is_expire;

  if (isFinal10Days) {
    storage.setItem(`${DAILY_KEY_PREFIX}${scope}_${todayStr}`, "true");
    return;
  }

  storage.setItem(`${MILESTONE_KEY_PREFIX}${scope}`, String(config.milestone));
  if (!token) return;
  try {
    await axios.post(
      `${apiUrl}brand/dismiss-expiry-banner`,
      { milestone: config.milestone },
      { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }
    );
  } catch (e) {}
};

const SubscriptionExpiryBanner = ({ planData, mode = "dashboard", onUpgradeClick, onRequestInvoice }) => {
  const plan = planData?.data || planData || {};
  const todayStr = getLocalDateString();
  const config = useMemo(() => getSubscriptionBannerConfig({ plan }), [planData]);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (plan.expiry_date && (plan.current_plan_key || plan.plan)) {
      cleanupExpiredDismissals(getDismissalScope(plan), todayStr);
    }
    setIsDismissed(checkIsDismissed({ config, plan, mode, todayStr }));
  }, [config, mode, planData, todayStr]);

  if (!config || isDismissed) return null;

  const hasUpcomingPaid = Boolean(plan.upcoming_subscription);
  const canDismiss = mode === "dashboard" && !hasUpcomingPaid && !plan.is_suspended && !plan.is_in_retry_grace;
  const isCancelled = plan.is_cancelled || plan.billing_status === "cancelled";
  const isExpired = !plan.is_in_retry_grace && (plan.is_expire || plan.billing_status === "expired" || plan.is_suspended);

  const handleDismiss = async () => {
    setIsDismissed(true);
    await dismissBannerStage({ config, plan, todayStr });
  };

  return (
    <div className="dashboard-subscription-banner subscription-expiry-banner">
      <div className="sub-banner-left">
        <div className={`sub-banner-icon-circle icon-${config.themeType}`}>
          <SubscriptionBannerIllustration type={config.iconType} size={22} />
        </div>
        <div className="sub-banner-content">
          <div className="sub-banner-header-row">
            <span className="sub-banner-title">{config.title}</span>
            <span className={`sub-banner-pill pill-${config.themeType}`}>{config.badge}</span>
            {plan.invoice_renewal && !hasUpcomingPaid && <span className="sub-banner-pill pill-subtle">Net 30 Invoice</span>}
          </div>
          <p className="sub-banner-desc">{config.description}</p>
        </div>
      </div>

      <div className="sub-banner-right">
        <div className="sub-banner-actions">
          {hasUpcomingPaid ? (
            onRequestInvoice && (
              <button type="button" className="btn-sub-primary btn-success-solid" onClick={onRequestInvoice}>
                View Renewal Details <ArrowRight size={13} className="ml-50" />
              </button>
            )
          ) : (
            <>
              {plan.upgrade_eligible && onUpgradeClick && (
                <button type="button" className="btn-sub-secondary" onClick={onUpgradeClick}>
                  {Number(plan.upgrade_discount_percent) > 0 ? `Upgrade & Save ${plan.upgrade_discount_percent}%` : "Upgrade to Annual"}
                </button>
              )}
              {onRequestInvoice && (
                <button type="button" className="btn-sub-primary" onClick={onRequestInvoice}>
                  Pay by Invoice <ArrowRight size={13} className="ml-50" />
                </button>
              )}
            </>
          )}

          {mode === "dashboard" ? (
            <Link to="/dashboard/brands/subscription-plan" className="btn-sub-secondary">
              Manage <ArrowRight size={13} className="ml-50" />
            </Link>
          ) : (
            (isCancelled || isExpired) && !hasUpcomingPaid && !plan.is_in_retry_grace && (
              <Link to="/subscription-plans" className="btn-sub-primary">
                Reactivate Plan <ArrowRight size={13} className="ml-50" />
              </Link>
            )
          )}
        </div>

        {canDismiss && (
          <button type="button" className="sub-banner-dismiss-btn" onClick={handleDismiss} aria-label="Dismiss notification" title="Dismiss notification">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

SubscriptionExpiryBanner.propTypes = {
  planData: PropTypes.object,
  mode: PropTypes.string,
  onUpgradeClick: PropTypes.func,
  onRequestInvoice: PropTypes.func,
};

export default SubscriptionExpiryBanner;
